import { Component, computed, ElementRef, input, model, signal, ViewChild } from "@angular/core";
import { MatTree, MatTreeNode, MatTreeNodeDef, MatTreeNodeToggle, MatTreeNodePadding } from "@angular/material/tree";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatCheckbox } from "@angular/material/checkbox";
import { CdkDrag, CdkDragHandle} from "@angular/cdk/drag-drop";
import { MatFormField, MatSuffix } from "@angular/material/form-field";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatInput } from "@angular/material/input";

type DisplayState = "unchecked" | "partial" | "self" | "all";

@Component({
    selector: "valben-tree-filter",
    templateUrl: "tree-filter.component.html",
    standalone: true,
    imports: [
        MatTree,
        MatTreeNode,
        MatTreeNodeDef,
        MatTreeNodeToggle,
        MatTreeNodePadding,
        MatIconButton,
        MatIcon,
        MatIconButton,
        MatIcon,
        CdkDrag,
        CdkDragHandle,
        MatInput,
        MatCheckbox,
        MatCard,
        MatCardContent,
        MatFormField,
        MatSuffix,
    ],
    styles: [`
        :host {
            display: block;
            position: relative;
            overflow-x: hidden;
            overflow-y: auto;
        }
    `],
})
export class TreeFilterComponent {
    values = input<any[] | null | undefined>([]);
    labelAccessor = input.required<(node: any) => string>();
    idAccessor = input.required<(node: any) => string>();
    threeStateCheckbox = input<boolean>(false);
    selected = model<string[] | null>([]);

    childrenAccessor = (node: any) => node.children ?? [];
    hasChild = (_: number, node: any) => this.childrenAccessor(node).length > 0;

    matches = signal<any[]>([]);
    currentMatchIndex = signal<number>(-1);
    searchTerm = signal<string>('');
    searchOpen = signal<boolean>(false);

    private _explicitChecked = computed(() => new Set(this.selected() ?? []));
    private _searchTimer: ReturnType<typeof setTimeout> | undefined;

    shownValues = computed(() => {
        const labelAccessor = this.labelAccessor();
        const idAccessor = this.idAccessor();

        const attach = (node: any): any => ({
            ...node,
            nodeId: idAccessor(node),
            children: node.children?.map(attach),
            label: labelAccessor(node),
            searchLabel: labelAccessor(node).toLowerCase(),
        });
        return (this.values() ?? []).map(attach);
    });

    nodeStates = computed(() => {
        const explicit = this._explicitChecked();
        const threeState = this.threeStateCheckbox();
        const shownValues = this.shownValues();
        const map = new Map<string, DisplayState>();

        for (const root of shownValues) {
            this._computeState(root, explicit, threeState, map);
        }
        return map;
    });

    matchedIds = computed(() => {
        const ids = new Set<string>();
        for (const match of this.matches()) {
            ids.add(match.node.nodeId);
        }
        return ids;
    });

    @ViewChild("tree") private _tree!: MatTree<any>;
    @ViewChild(CdkDrag) private _cardDrag?: CdkDrag;
    @ViewChild("searchInput") private _searchInput?: ElementRef<HTMLInputElement>;

    constructor(private _elRef: ElementRef<HTMLElement>) {}

    searchTree(value: string): void {
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
            const query = value.trim().toLowerCase();
            if (!query) {
                this.matches.set([]);
                this.currentMatchIndex.set(-1);
                return;
            }
            const found: any[] = [];
            const walk = (nodes: any[], path: any[]) => {
                for (const node of nodes) {
                    if (node.searchLabel.includes(query)) {
                        found.push({ node, path });
                    }
                    const children = this.childrenAccessor(node);
                    if (children.length) walk(children, [...path, node]);
                }
            };
            walk(this.shownValues(), []);
            this.matches.set(found);

            if (found.length) {
                this.currentMatchIndex.set(0);
                this._scrollToMatch(0);
            } else {
                this.currentMatchIndex.set(-1);
            }
        }, 300);
    }

    goToNext(): void {
        const matches = this.matches();
        if (!matches.length) return;
        const next = (this.currentMatchIndex() + 1) % matches.length;
        this.currentMatchIndex.set(next);
        this._scrollToMatch(next);
    }

    goToPrevious(): void {
        const matches = this.matches();
        if (!matches.length) return;
        const prev = (this.currentMatchIndex() - 1 + matches.length) % matches.length;
        this.currentMatchIndex.set(prev);
        this._scrollToMatch(prev);
    }

    ngOnDestroy(): void {
        clearTimeout(this._searchTimer);
    }

    cycleParent(node: any): void {
        const id = node.nodeId;
        const current = this.nodeStates().get(id) ?? "unchecked";
        const set = new Set(this._explicitChecked());

        if (current === "all") set.delete(id);
        else set.add(id);

        if (current === "unchecked" || current === "all") this._clearDescendants(node, set);
        else this._markDescendants(node, set);

        this.selected.set([...set]);

        // Prvi klik odabire samo ovu jedinicu; otvori granu da se vidi da podređene nisu označene.
        if (current === "unchecked") this._tree.expand(node);
    }

    toggleSimple(node: any): void {
        const id = node.nodeId;
        const currentState = this.nodeStates().get(id) ?? "unchecked";
        const set = new Set(this._explicitChecked());

        if (currentState !== "all") {
            set.add(id);
            this._markDescendants(node, set);
        } else {
            set.delete(id);
            this._clearDescendants(node, set);
        }

        this.selected.set([...set]);
    }

    private _computeState(
        node: any,
        set: Set<string>,
        threeState: boolean,
        map?: Map<string, DisplayState>
    ): DisplayState {
        const id = node.nodeId;
        const children = this.childrenAccessor(node);

        if (children.length === 0) {
            const state: DisplayState = set.has(id) ? "all" : "unchecked";
            map?.set(id, state);
            return state;
        }

        const childStates: DisplayState[] = children.map((c: any) =>
            this._computeState(c, set, threeState, map)
        );

        let state: DisplayState;

        if (!threeState) {
            if (set.has(id)) {
                state = childStates.some(s => s === "unchecked" || s === "partial") ? "partial" : "all";
            } else if (childStates.some(s => s === "partial" || s === "all")) {
                state = "partial";
            } else {
                state = "unchecked";
            }
        } else {
            const allCheckedLike = childStates.every(s => s === "all");
            const noneCheckedLike = childStates.every(s => s === "unchecked");

            if (allCheckedLike) state = set.has(id) ? "all" : "partial";
            else if (noneCheckedLike) state = set.has(id) ? "self" : "unchecked";
            else state = "partial";
        }

        map?.set(id, state);
        return state;
    }

    private _clearDescendants(node: any, set: Set<string>): void {
        for (const child of this.childrenAccessor(node)) {
            set.delete(child.nodeId);
            this._clearDescendants(child, set);
        }
    }

    private _markDescendants(node: any, set: Set<string>): void {
        for (const child of this.childrenAccessor(node)) {
            set.add(child.nodeId);
            this._markDescendants(child, set);
        }
    }

    private _scrollToMatch(index: number): void {
        const match = this.matches()[index];
        if (!match) return;

        for (const ancestor of match.path) {
            if (!this._tree.isExpanded(ancestor)) this._tree.expand(ancestor);
        }

        setTimeout(() => {
            const id = match.node.nodeId;
            const els = this._elRef.nativeElement.querySelectorAll<HTMLElement>('[data-node-id]');
            for (const el of Array.from(els)) {
                if (el.dataset["nodeId"] === id) {
                    el.scrollIntoView({ block: "center" });
                    break;
                }
            }
        });
    }

    closeSearch(): void {
        this.searchOpen.set(false);
        this.searchTerm.set("");
        this.matches.set([]);
        this.currentMatchIndex.set(-1);
        this._cardDrag?.reset();
    }

    openSearch(): void {
        this.searchOpen.set(true);
        setTimeout(() => this._searchInput?.nativeElement.focus());
    }
}
